/**
 * buildThumbnailPayload
 *
 * Some MP4s are NOT "faststart": their `moov` metadata atom sits at the END of
 * the file. The server thumbnail endpoint (FFmpeg) needs `moov` to parse the
 * video, but it also enforces an upload size limit — so we can neither send just
 * the first 5MB (misses the trailing `moov`) nor the whole file (413 Too Large).
 *
 * This builds a small, self-consistent MP4 the server CAN parse:
 *     [ ftyp ... ][ mdat header + first frames (truncated) ][ moov ]
 * The `mdat` box size is rewritten to the truncated length so `moov` lands right
 * after it, and the retained frame bytes keep their ORIGINAL absolute offsets, so
 * `moov`'s sample offsets (stco/co64) into those early frames stay valid — enough
 * for the server to extract an early thumbnail frame.
 *
 * Returns a Blob (~<= TARGET_BYTES) for a trimmable moov-at-end MP4, or null when
 * trimming isn't needed / possible (faststart file, non-MP4, parse failure) — in
 * which case the caller should fall back to sending the first 5MB.
 */

// The uploader (nginx) rejects bodies at/over ~1MB (413), so the whole payload —
// including multipart overhead — must stay comfortably under 1MB. Callers should
// also cap their plain first-N-bytes fallback slice to this value.
export const THUMBNAIL_MAX_BYTES = 900 * 1024; // ~900KB, safely under the 1MB limit
const TARGET_BYTES = THUMBNAIL_MAX_BYTES;
const MIN_HEAD_DATA = 128 * 1024; // always keep at least this many mdat bytes

async function readAtomHeader(file, pos) {
  const buf = await file.slice(pos, pos + 16).arrayBuffer();
  if (buf.byteLength < 8) return null;
  const dv = new DataView(buf);
  let size = dv.getUint32(0);
  const type = String.fromCharCode(dv.getUint8(4), dv.getUint8(5), dv.getUint8(6), dv.getUint8(7));
  let headerSize = 8;
  if (size === 1) {
    if (buf.byteLength < 16) return null;
    const hi = dv.getUint32(8);
    const lo = dv.getUint32(12);
    size = (hi * 4294967296) + lo; // 64-bit largesize
    headerSize = 16;
  }
  return { type, size, headerSize, pos };
}

export async function buildThumbnailPayload(file) {
  try {
    if (!file || typeof file.slice !== 'function' || file.size < 1024) return null;

    // Walk the top-level atoms (only headers are read — the huge mdat is skipped).
    const atoms = {};
    let pos = 0;
    let guard = 0;
    while (pos < file.size && guard < 64) {
      guard += 1;
      // eslint-disable-next-line no-await-in-loop
      const a = await readAtomHeader(file, pos);
      if (!a || a.size <= 0) break;
      if (!atoms[a.type]) atoms[a.type] = a;
      pos += a.size;
    }

    const { ftyp, mdat, moov } = atoms;
    if (!ftyp || !mdat || !moov) return null; // not a (parseable) MP4
    // Faststart / small file: the first 5MB already contains moov — no trim needed.
    if (moov.pos < TARGET_BYTES) return null;

    const mdatDataStart = mdat.pos + mdat.headerSize;
    const mdatDataAvail = mdat.size - mdat.headerSize;

    // Budget for early mdat frames = TARGET - (bytes up to mdat data) - moov size.
    let headData = TARGET_BYTES - mdatDataStart - moov.size;
    if (headData < MIN_HEAD_DATA) headData = MIN_HEAD_DATA;
    if (headData > mdatDataAvail) headData = mdatDataAvail;

    const headEnd = mdatDataStart + headData;
    const headBuf = await file.slice(0, headEnd).arrayBuffer();
    const dv = new DataView(headBuf);

    // Rewrite the mdat box size to the truncated length so moov follows it cleanly.
    const newMdatBoxSize = mdat.headerSize + headData;
    if (mdat.headerSize === 8) {
      dv.setUint32(mdat.pos, newMdatBoxSize);
    } else {
      // 64-bit largesize lives at mdat.pos + 8 (after the 32-bit size==1 marker).
      dv.setUint32(mdat.pos + 8, Math.floor(newMdatBoxSize / 4294967296));
      dv.setUint32(mdat.pos + 12, newMdatBoxSize >>> 0);
    }

    const moovBuf = await file.slice(moov.pos, moov.pos + moov.size).arrayBuffer();
    return new Blob([headBuf, moovBuf], { type: file.type || 'video/mp4' });
  } catch (e) {
    return null;
  }
}

export default buildThumbnailPayload;
